import {
  FC,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  EditorState,
  EditorThemeClasses,
  FOCUS_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
} from "lexical";
import { $findMatchingParent, $getNearestNodeOfType } from "@lexical/utils";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
} from "@lexical/list";
import { $setBlocksType } from "@lexical/selection";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  ORDERED_LIST,
  STRIKETHROUGH,
  UNORDERED_LIST,
} from "@lexical/markdown";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { logDebug, logError } from "../utils/log";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { classNames } from "../utils/classNames";
import { VBox } from "../components-v2/Box";
import { DropdownMenu, DropdownMenuItems } from "../components-v2/Dropdown";
import { Icon, IconProps } from "../components-v2/Icon";
import { IconName } from "../components-v2/iconMapping";
import { renderIcon } from "../components-v2/iconHelpers";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { useTranslation } from "../stores/RootStoreContext";
import { useElementId } from "../utils/reactHelpers";
import { DCVariableNode, $createDCVariableNode } from "./DCVariableNode";

import { TMessage } from "../i18n/tt";
import { DCVariableTransformer } from "./DCVariableTransformer";

const ToolbarButton = forwardRef<
  HTMLButtonElement,
  {
    tooltip: string;
    isSelected?: boolean;
    isDisabled?: boolean;
    onClick?: () => void;
    children: ReactNode;
  }
>(({ tooltip, isSelected, isDisabled, onClick, children }, ref) => {
  return (
    <button
      ref={ref}
      type={"button"}
      className={classNames(
        "tw-inline-flex",
        "tw-items-center",
        "tw-leading-none",
        "tw-gap-1",
        "tw-p-2",
        "tw-border-2",
        "tw-rounded",
        isDisabled && "tw-opacity-50",
        !isDisabled && !isSelected && ["hover:bg-gray-x-soft", "hover:tw-border-light-grey"],
        isSelected ? ["bg-gray", "border-gray"] : ["border-transparent"],
      )}
      tabIndex={-1}
      disabled={isDisabled}
      aria-pressed={isSelected}
      aria-label={tooltip}
      onClick={onClick}
      data-tooltip={tooltip}
    >
      {children}
    </button>
  );
});

export type ToolbarDropdown = {
  icon: IconName | IconProps;
  tooltip: TMessage;
  label?: TMessage;
  items: DropdownMenuItems;
};

const Toolbar: FC<{
  isDisabled?: boolean;
  customOptions?: ToolbarDropdown[];
}> = ({ isDisabled, customOptions }) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [blockType, setBlockType] = useState<string | undefined>();

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      // noinspection TypeScriptValidateJSTypes
      const elementDOM = editor.getElementByKey(elementKey);

      // noinspection TypeScriptValidateJSTypes
      setIsBold(selection.hasFormat("bold"));
      // noinspection TypeScriptValidateJSTypes
      setIsItalic(selection.hasFormat("italic"));

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = element.getType();
          setBlockType(type);
        }
      }
    }
  }, [editor]);

  const formatParagraph = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  }, [editor]);

  const formatBold = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold"),
    [editor],
  );

  const formatItalic = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic"),
    [editor],
  );

  const formatUnorderedList = useCallback(() => {
    if (blockType === "bullet") {
      formatParagraph();
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, blockType, formatParagraph]);

  const formatOrderedList = useCallback(() => {
    if (blockType === "number") {
      formatParagraph();
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, blockType, formatParagraph]);

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      }),
    [editor, updateToolbar],
  );

  return (
    <div className={"tw-flex tw-p-1 tw-border-b border-gray tw-justify-between"}>
      <div className={"tw-gap-1"}>
        <ToolbarButton
          tooltip={t("richTextEditor.bold")}
          isSelected={isBold}
          onClick={formatBold}
          isDisabled={isDisabled}
        >
          <Icon name={"text-bolder"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.italic")}
          isSelected={isItalic}
          onClick={formatItalic}
          isDisabled={isDisabled}
        >
          <Icon name={"text-italic"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.bulletList")}
          isSelected={blockType === "bullet"}
          onClick={formatUnorderedList}
          isDisabled={isDisabled}
        >
          <Icon name={"list-bullets"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.numberedList")}
          isSelected={blockType === "number"}
          onClick={formatOrderedList}
          isDisabled={isDisabled}
        >
          <Icon name={"list-numbers"} />
        </ToolbarButton>
      </div>
      <div className={"tw-gap-1"}>
        {customOptions?.map((c) => (
          <DropdownMenu
            key={t(c.tooltip)}
            button={({ ref }) => (
              <ToolbarButton ref={ref} tooltip={t(c.tooltip)} isDisabled={isDisabled}>
                {renderIcon(c.icon)}
                {c.label && <div>{t(c.label)}</div>}
              </ToolbarButton>
            )}
            items={c.items}
            alignment={"start"}
          />
        ))}
      </div>
    </div>
  );
};

const FocusPlugin: FC<{
  onFocus?: (editorState: EditorState, event: FocusEvent) => void;
  onBlur?: (editorState: EditorState, event: FocusEvent) => void;
}> = ({ onFocus, onBlur }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerCommand(
        BLUR_COMMAND,
        (event: FocusEvent) => {
          onBlur?.(editor.getEditorState(), event);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, onBlur],
  );

  useEffect(
    () =>
      editor.registerCommand(
        FOCUS_COMMAND,
        (event: FocusEvent) => {
          onFocus?.(editor.getEditorState(), event);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, onFocus],
  );

  return null;
};

const ReadOnlyController: FC<{ readOnly?: boolean }> = ({ readOnly }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  return null;
};

const changeNotificationDelay = 10_000;

const ChangePlugin = ({
  editorId,
  value,
  onChange,
  onInput,
}: {
  editorId: string;
  value: string;
  onChange: (value: string) => void;
  onInput?: () => void;
}) => {
  const [editor] = useLexicalComposerContext();
  const savePoint = useRef<string>();
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>();
  const focused = useRef(false);

  const clearDelayedChangeNotification = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };

  const notifyChanged = useCallback(() => {
    editor.getEditorState().read(() => {
      const v = $convertToMarkdownString(transformers);
      savePoint.current = v;
      if (v.trim() !== value.trim()) {
        logDebug("MarkdownEditor::ChangePlugin: notifying change", editorId);
        onChange(v);
      }
    });
  }, [editor, editorId, onChange, value]);

  const handleDelayedChange = useCallback(() => {
    clearDelayedChangeNotification();
    timeoutId.current = setTimeout(() => {
      notifyChanged();
    }, changeNotificationDelay);
  }, [notifyChanged]);

  const handleChange = useCallback(
    (_editorState: EditorState, _editor: LexicalEditor, tags: Set<string>) => {
      if (!tags.has("re-init")) {
        onInput?.();
        handleDelayedChange();
      }
    },
    [onInput, handleDelayedChange],
  );

  const handleFocus = useCallback(() => {
    logDebug("MarkdownEditor::ChangePlugin: focused", editorId);
    focused.current = true;
  }, [editorId]);

  const handleBlur = useCallback(() => {
    logDebug("MarkdownEditor::ChangePlugin: blurred", editorId);
    focused.current = false;
    clearDelayedChangeNotification();
    notifyChanged();
  }, [editorId, notifyChanged]);

  useEffect(() => {
    if (savePoint.current === undefined) {
      savePoint.current = value;
    } else if (value.trim() !== savePoint.current?.trim()) {
      logDebug("MarkdownEditor: detected external change, re-initializing...", {
        lastNotifiedValue: savePoint.current,
        incomingValue: value,
        editorId,
      });
      editor.update(
        () => {
          $convertFromMarkdownString(value, transformers);
          savePoint.current = value;
          if (!focused.current) {
            // prevent stealing focus if the editor wasn't already focused
            $setSelection(null);
          }
        },
        { tag: "re-init" },
      );
    }
  }, [editor, editorId, value]);

  useEffect(() => {
    logDebug("MarkdownEditor::ChangePlugin: mounted", editorId);
    return () => {
      logDebug("MarkdownEditor::ChangePlugin: unmounted", editorId);
      clearDelayedChangeNotification();
    };
  }, [editorId]);

  return (
    <>
      <OnChangePlugin onChange={handleChange} ignoreHistoryMergeTagChange ignoreSelectionChange />
      <FocusPlugin onFocus={handleFocus} onBlur={handleBlur} />
    </>
  );
};

const MarkdownEditorApiPlugin = ({
  editorRef,
}: {
  editorRef?: (editorApi: MarkdownEditorApi) => void;
}) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef?.({
      insertVariable: (name) => {
        editor.update(() => {
          const selection = $getSelection();
          selection?.insertNodes([$createDCVariableNode(name), $createTextNode(" ")]);
        });
      },
    });
  }, [editor, editorRef]);

  return null;
};

const transformers = [
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  STRIKETHROUGH,
  UNORDERED_LIST,
  ORDERED_LIST,
  DCVariableTransformer,
];

const nodes = [HorizontalRuleNode, ListNode, ListItemNode, DCVariableNode];

export type MarkdownEditorApi = {
  insertVariable: (variableName: string) => void;
};

type MarkdownEditorProps = {
  label?: string | ReactNode;
  placeholder?: string;
  value: string;
  /** Is fired whenever the user types inside the editor, just to let you know that the user is making changes */
  onInput?: () => void;
  /** Is fired either when the component loses focus, or when the user has been idle for some specific time (see `idleTimeout`) */
  onChange: (value: string) => void;
  /** The number of milliseconds of the user being idle before a change should be notified, defaults to 5000 (5s) */
  idleTimeout?: number;
  testId?: string;
  readOnly?: boolean;
  customOptions?: ToolbarDropdown[];
  size?: "auto" | "full";
  autofocus?: boolean;
  editorRef?: (editor: MarkdownEditorApi) => void;
};

let currentEditorId = 1;

const useEditorId = () => useMemo(() => `editor-${currentEditorId++}`, []);

export function MarkdownEditor({
  label,
  placeholder,
  value,
  onInput,
  onChange,
  testId,
  readOnly,
  customOptions,
  size = "auto",
  autofocus,
  editorRef,
}: MarkdownEditorProps) {
  const editorId = useEditorId();
  const labelId = useElementId();
  const theme: EditorThemeClasses = {
    text: {
      // bold: "tw-font-bold",
      // italic: "tw-font-italic",
    },
  };

  const handleClick = useCallback(() => {
    document.getElementById(editorId)?.focus();
  }, [editorId]);

  const handleError = useCallback((err: Error) => {
    logError(err, "Error in MarkdownEditor");
  }, []);

  return (
    <VBox
      data-test-id={testId}
      htmlProps={{ onClick: handleClick }}
      className={classNames(size === "full" && ["tw-h-full", "tw-w-full"])}
    >
      {label && <label id={labelId}>{label}</label>}
      <div
        className={classNames(
          "tw-relative",
          "tw-flex",
          "tw-flex-col",
          "tw-grow",
          "tw-border",
          "border-gray",
          "tw-rounded",
          "color-default",
          "bg-default",
          "focus-within:tw-ring-1",
          "focus-within:border-primary",
          "focus-within:ring-primary",
          "hover:bg-border-gray-hard",
          "hover:focus-within:border-primary",
          size === "auto" && "tw-min-h-[10rem]",
          size === "full" && "tw-overflow-hidden",
        )}
      >
        <LexicalComposer
          initialConfig={{
            editorState: value ? () => $convertFromMarkdownString(value, transformers) : undefined,
            namespace: "MarkdownEditor",
            onError: handleError,
            theme: theme,
            nodes: nodes,
            editable: !readOnly,
          }}
        >
          <MarkdownEditorApiPlugin editorRef={editorRef} />
          <MarkdownShortcutPlugin transformers={transformers} />
          <Toolbar isDisabled={readOnly} customOptions={customOptions} />
          <RichTextPlugin
            contentEditable={
              <div className={classNames("tw-grow", size === "full" && "tw-overflow-auto")}>
                <ContentEditable
                  className={classNames("tw-outline-none tw-px-4 tw-mt-4 content")}
                  ariaLabelledBy={labelId}
                  id={editorId}
                />
              </div>
            }
            placeholder={
              <div
                className={classNames(
                  "tw-absolute",
                  "tw-pointer-events-none",
                  "tw-opacity-50",
                  "tw-top-16",
                  "tw-left-4",
                )}
              >
                {placeholder ?? ""}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <HistoryPlugin />
          <ReadOnlyController readOnly={readOnly} />
          <ChangePlugin value={value} onChange={onChange} onInput={onInput} editorId={editorId} />
          {autofocus ? <AutoFocusPlugin /> : <></>}
        </LexicalComposer>
      </div>
    </VBox>
  );
}
